import React, { useState, useEffect, useCallback } from 'react';
import { 
  Home, FileText, Users, Briefcase, Newspaper, Settings, 
  Upload, Save, Plus, Trash2, Edit, Eye, LogOut, Menu, X,
  Image, Video, Building, Phone, Mail, MapPin, Globe,
  Star, Shield, UserCheck, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth helper
const getAuthHeader = (username, password) => {
  return 'Basic ' + btoa(`${username}:${password}`);
};

// Sidebar Navigation
const Sidebar = ({ activeSection, setActiveSection, isOpen, setIsOpen }) => {
  const menuItems = [
    { id: 'hero', label: 'Hero Section', icon: Home },
    { id: 'services', label: 'Services', icon: FileText },
    { id: 'about', label: 'About / Über uns', icon: Users },
    { id: 'partners', label: 'Partners', icon: Building },
    { id: 'news', label: 'News', icon: Newspaper },
    { id: 'jobs', label: 'Jobs / Karriere', icon: Briefcase },
    { id: 'contact-info', label: 'Contact Info', icon: Phone },
    { id: 'settings', label: 'Company Settings', icon: Settings },
    { id: 'media', label: 'Media Library', icon: Image },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#003366] transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h1 className="text-xl font-bold text-white">CMS</h1>
          <button onClick={() => setIsOpen(false)} className="lg:hidden text-white/60 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <nav className="p-4 space-y-1">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveSection(item.id); setIsOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeSection === item.id
                  ? 'bg-[#D4AF37] text-[#003366]'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <item.icon size={20} />
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>
    </>
  );
};

// Input Components
const InputField = ({ label, value, onChange, placeholder, type = 'text', rows = 3 }) => (
  <div className="space-y-1">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    {type === 'textarea' ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent resize-none"
      />
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
      />
    )}
  </div>
);

const ImageUpload = ({ label, value, onChange, auth }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/cms/upload`, {
        method: 'POST',
        headers: { 'Authorization': auth },
        body: formData
      });
      const data = await res.json();
      if (data.url) {
        onChange(`${API_URL}${data.url}`);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Upload failed:', err);
      }
    }
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter URL or upload file"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
        />
        <label className="px-4 py-2 bg-[#003366] text-white rounded-lg cursor-pointer hover:bg-[#002244] transition-colors flex items-center gap-2">
          <Upload size={16} />
          {uploading ? 'Uploading...' : 'Upload'}
          <input type="file" accept="image/*,video/*" onChange={handleUpload} className="hidden" />
        </label>
      </div>
      {value && (
        <div className="mt-2">
          {value.includes('.mp4') || value.includes('.webm') ? (
            <video src={value} className="w-32 h-20 object-cover rounded" controls />
          ) : (
            <img src={value} alt="Preview" className="w-32 h-20 object-cover rounded" />
          )}
        </div>
      )}
    </div>
  );
};

// Section Components
const HeroSection = ({ auth }) => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/cms/hero`).then(r => r.json()).then(setData);
  }, []);

  const save = async () => {
    setSaving(true);
    await fetch(`${API_URL}/api/cms/hero`, {
      method: 'PUT',
      headers: { 'Authorization': auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    setSaving(false);
    alert('Saved!');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Hero Section</h2>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#003366] rounded-lg font-medium hover:bg-[#c4a030]">
          <Save size={18} />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4 p-6 bg-white rounded-xl shadow-sm border">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">🇩🇪 German</h3>
          <InputField label="Headline" value={data.headline_de} onChange={v => setData({...data, headline_de: v})} />
          <InputField label="Subheadline" value={data.subheadline_de} onChange={v => setData({...data, subheadline_de: v})} />
          <InputField label="Description" value={data.description_de} onChange={v => setData({...data, description_de: v})} type="textarea" />
          <InputField label="CTA Button" value={data.cta_de} onChange={v => setData({...data, cta_de: v})} />
        </div>
        <div className="space-y-4 p-6 bg-white rounded-xl shadow-sm border">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">🇬🇧 English</h3>
          <InputField label="Headline" value={data.headline_en} onChange={v => setData({...data, headline_en: v})} />
          <InputField label="Subheadline" value={data.subheadline_en} onChange={v => setData({...data, subheadline_en: v})} />
          <InputField label="Description" value={data.description_en} onChange={v => setData({...data, description_en: v})} type="textarea" />
          <InputField label="CTA Button" value={data.cta_en} onChange={v => setData({...data, cta_en: v})} />
        </div>
      </div>
      
      <div className="p-6 bg-white rounded-xl shadow-sm border">
        <ImageUpload label="Background Video URL" value={data.video_url} onChange={v => setData({...data, video_url: v})} auth={auth} />
      </div>

      {/* Bottom Section Text */}
      <div className="p-6 bg-white rounded-xl shadow-sm border space-y-4">
        <h3 className="font-semibold text-gray-800 border-b pb-2">Bottom Section (Scroll Indicator)</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <InputField label="Title (DE)" value={data.bottom_title_de} onChange={v => setData({...data, bottom_title_de: v})} placeholder="z.B. Unsere Dienstleistungen" />
          <InputField label="Title (EN)" value={data.bottom_title_en} onChange={v => setData({...data, bottom_title_en: v})} placeholder="e.g. Our Services" />
          <InputField label="Subtitle (DE)" value={data.bottom_subtitle_de} onChange={v => setData({...data, bottom_subtitle_de: v})} placeholder="z.B. Wir bieten" />
          <InputField label="Subtitle (EN)" value={data.bottom_subtitle_en} onChange={v => setData({...data, bottom_subtitle_en: v})} placeholder="e.g. We offer" />
        </div>
      </div>
    </div>
  );
};

const ServicesSection = ({ auth }) => {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadServices = useCallback(() => {
    fetch(`${API_URL}/api/cms/services`).then(r => r.json()).then(setServices);
  }, []);

  useEffect(() => { loadServices(); }, [loadServices]);

  const save = async (service) => {
    setSaving(true);
    const method = services.find(s => s.id === service.id) ? 'PUT' : 'POST';
    const url = method === 'PUT' ? `${API_URL}/api/cms/services/${service.id}` : `${API_URL}/api/cms/services`;
    await fetch(url, {
      method,
      headers: { 'Authorization': auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(service)
    });
    setSaving(false);
    setEditing(null);
    loadServices();
  };

  const deleteService = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await fetch(`${API_URL}/api/cms/services/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': auth }
    });
    loadServices();
  };

  const newService = () => {
    setEditing({
      id: crypto.randomUUID(),
      title_de: '', title_en: '',
      description_de: '', description_en: '',
      image_url: '', video_url: '', icon: 'sparkles', order: services.length
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Services</h2>
        <button onClick={newService} className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded-lg font-medium hover:bg-[#002244]">
          <Plus size={18} />Add Service
        </button>
      </div>

      {editing && (
        <div className="p-6 bg-white rounded-xl shadow-lg border-2 border-[#D4AF37]">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <InputField label="Title (DE)" value={editing.title_de} onChange={v => setEditing({...editing, title_de: v})} />
            <InputField label="Title (EN)" value={editing.title_en} onChange={v => setEditing({...editing, title_en: v})} />
            <InputField label="Description (DE)" value={editing.description_de} onChange={v => setEditing({...editing, description_de: v})} type="textarea" />
            <InputField label="Description (EN)" value={editing.description_en} onChange={v => setEditing({...editing, description_en: v})} type="textarea" />
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <ImageUpload label="Image URL" value={editing.image_url} onChange={v => setEditing({...editing, image_url: v})} auth={auth} />
            <ImageUpload label="Video URL" value={editing.video_url} onChange={v => setEditing({...editing, video_url: v})} auth={auth} />
          </div>
          <div className="flex gap-2">
            <button onClick={() => save(editing)} disabled={saving} className="px-4 py-2 bg-[#D4AF37] text-[#003366] rounded-lg font-medium">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {services.map(service => (
          <div key={service.id} className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm border hover:border-[#D4AF37] transition-colors">
            <div className="flex items-center gap-4">
              {service.image_url && <img src={service.image_url} alt="" className="w-16 h-12 object-cover rounded" />}
              <div>
                <h4 className="font-medium text-gray-900">{service.title_de || 'Untitled'}</h4>
                <p className="text-sm text-gray-500">{service.title_en}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(service)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit size={18} /></button>
              <button onClick={() => deleteService(service.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
        {services.length === 0 && <p className="text-center text-gray-500 py-8">No services yet. Click "Add Service" to create one.</p>}
      </div>
    </div>
  );
};

const AboutSection = ({ auth }) => {
  const [data, setData] = useState({ bio_de: [], bio_en: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/cms/about`).then(r => r.json()).then(d => setData({...d, bio_de: d.bio_de || [], bio_en: d.bio_en || []}));
  }, []);

  const save = async () => {
    setSaving(true);
    await fetch(`${API_URL}/api/cms/about`, {
      method: 'PUT',
      headers: { 'Authorization': auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    setSaving(false);
    alert('Saved!');
  };

  const updateBio = (lang, index, value) => {
    const key = `bio_${lang}`;
    const newBio = [...(data[key] || [])];
    newBio[index] = value;
    setData({...data, [key]: newBio});
  };

  const addBioParagraph = (lang) => {
    const key = `bio_${lang}`;
    setData({...data, [key]: [...(data[key] || []), '']});
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">About / Über uns</h2>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#003366] rounded-lg font-medium">
          <Save size={18} />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="p-6 bg-white rounded-xl shadow-sm border space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <InputField label="Name" value={data.name} onChange={v => setData({...data, name: v})} />
          <div className="grid grid-cols-2 gap-4">
            <InputField label="Role (DE)" value={data.role_de} onChange={v => setData({...data, role_de: v})} />
            <InputField label="Role (EN)" value={data.role_en} onChange={v => setData({...data, role_en: v})} />
          </div>
        </div>
        <ImageUpload label="Photo" value={data.image_url} onChange={v => setData({...data, image_url: v})} auth={auth} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="p-6 bg-white rounded-xl shadow-sm border space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">🇩🇪 Biography (German)</h3>
            <button onClick={() => addBioParagraph('de')} className="text-sm text-[#D4AF37] hover:underline">+ Add Paragraph</button>
          </div>
          {(data.bio_de || []).map((p, i) => (
            <InputField key={`bio-de-${i}`} label={`Paragraph ${i + 1}`} value={p} onChange={v => updateBio('de', i, v)} type="textarea" rows={4} />
          ))}
        </div>
        <div className="p-6 bg-white rounded-xl shadow-sm border space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">🇬🇧 Biography (English)</h3>
            <button onClick={() => addBioParagraph('en')} className="text-sm text-[#D4AF37] hover:underline">+ Add Paragraph</button>
          </div>
          {(data.bio_en || []).map((p, i) => (
            <InputField key={`bio-en-${i}`} label={`Paragraph ${i + 1}`} value={p} onChange={v => updateBio('en', i, v)} type="textarea" rows={4} />
          ))}
        </div>
      </div>
    </div>
  );
};

const PartnersSection = ({ auth }) => {
  const [partners, setPartners] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadPartners = useCallback(() => {
    fetch(`${API_URL}/api/cms/partners`).then(r => r.json()).then(setPartners);
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);

  const save = async (partner) => {
    const method = partners.find(p => p.id === partner.id) ? 'PUT' : 'POST';
    const url = method === 'PUT' ? `${API_URL}/api/cms/partners/${partner.id}` : `${API_URL}/api/cms/partners`;
    await fetch(url, { method, headers: { 'Authorization': auth, 'Content-Type': 'application/json' }, body: JSON.stringify(partner) });
    setEditing(null);
    loadPartners();
  };

  const deletePartner = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    await fetch(`${API_URL}/api/cms/partners/${id}`, { method: 'DELETE', headers: { 'Authorization': auth } });
    loadPartners();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Partners</h2>
        <button onClick={() => setEditing({ id: crypto.randomUUID(), name: '', logo_url: '', website_url: '', is_sponsor: false, description_de: '', description_en: '', order: partners.length })} className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded-lg font-medium">
          <Plus size={18} />Add Partner
        </button>
      </div>

      {editing && (
        <div className="p-6 bg-white rounded-xl shadow-lg border-2 border-[#D4AF37] space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <InputField label="Name" value={editing.name} onChange={v => setEditing({...editing, name: v})} />
            <InputField label="Website URL" value={editing.website_url} onChange={v => setEditing({...editing, website_url: v})} />
          </div>
          <ImageUpload label="Logo" value={editing.logo_url} onChange={v => setEditing({...editing, logo_url: v})} auth={auth} />
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={editing.is_sponsor} onChange={e => setEditing({...editing, is_sponsor: e.target.checked})} className="w-4 h-4" />
            <span className="text-sm font-medium">Is Sponsor (featured)</span>
          </label>
          {editing.is_sponsor && (
            <div className="grid md:grid-cols-2 gap-4">
              <InputField label="Description (DE)" value={editing.description_de} onChange={v => setEditing({...editing, description_de: v})} type="textarea" />
              <InputField label="Description (EN)" value={editing.description_en} onChange={v => setEditing({...editing, description_en: v})} type="textarea" />
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={() => save(editing)} className="px-4 py-2 bg-[#D4AF37] text-[#003366] rounded-lg font-medium">Save</button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        {partners.map(p => (
          <div key={p.id} className={`p-4 bg-white rounded-xl shadow-sm border hover:border-[#D4AF37] transition-colors ${p.is_sponsor ? 'border-[#D4AF37] bg-[#D4AF37]/5' : ''}`}>
            {p.logo_url && <img src={p.logo_url} alt={p.name} className="w-full h-20 object-contain mb-3" />}
            <h4 className="font-medium text-gray-900">{p.name}</h4>
            {p.is_sponsor && <span className="text-xs text-[#D4AF37] font-medium">★ Sponsor</span>}
            <div className="flex gap-2 mt-3">
              <button onClick={() => setEditing(p)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit size={16} /></button>
              <button onClick={() => deletePartner(p.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const NewsSection = ({ auth }) => {
  const [news, setNews] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadNews = useCallback(() => {
    fetch(`${API_URL}/api/cms/news`).then(r => r.json()).then(setNews);
  }, []);

  useEffect(() => { loadNews(); }, [loadNews]);

  const save = async (item) => {
    const method = news.find(n => n.id === item.id) ? 'PUT' : 'POST';
    const url = method === 'PUT' ? `${API_URL}/api/cms/news/${item.id}` : `${API_URL}/api/cms/news`;
    await fetch(url, { method, headers: { 'Authorization': auth, 'Content-Type': 'application/json' }, body: JSON.stringify(item) });
    setEditing(null);
    loadNews();
  };

  const deleteNews = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    await fetch(`${API_URL}/api/cms/news/${id}`, { method: 'DELETE', headers: { 'Authorization': auth } });
    loadNews();
  };

  const newArticle = () => setEditing({
    id: crypto.randomUUID(),
    title_de: '', title_en: '',
    excerpt_de: '', excerpt_en: '',
    content_de: '', content_en: '',
    category_de: '', category_en: '',
    image_url: '',
    date: new Date().toISOString().split('T')[0],
    published: true
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">News</h2>
        <button onClick={newArticle} className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded-lg font-medium">
          <Plus size={18} />Add Article
        </button>
      </div>

      {editing && (
        <div className="p-6 bg-white rounded-xl shadow-lg border-2 border-[#D4AF37] space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <InputField label="Title (DE)" value={editing.title_de} onChange={v => setEditing({...editing, title_de: v})} />
            <InputField label="Title (EN)" value={editing.title_en} onChange={v => setEditing({...editing, title_en: v})} />
            <InputField label="Category (DE)" value={editing.category_de} onChange={v => setEditing({...editing, category_de: v})} />
            <InputField label="Category (EN)" value={editing.category_en} onChange={v => setEditing({...editing, category_en: v})} />
            <InputField label="Excerpt (DE)" value={editing.excerpt_de} onChange={v => setEditing({...editing, excerpt_de: v})} type="textarea" />
            <InputField label="Excerpt (EN)" value={editing.excerpt_en} onChange={v => setEditing({...editing, excerpt_en: v})} type="textarea" />
          </div>
          <ImageUpload label="Image" value={editing.image_url} onChange={v => setEditing({...editing, image_url: v})} auth={auth} />
          <div className="grid md:grid-cols-2 gap-4">
            <InputField label="Date" value={editing.date} onChange={v => setEditing({...editing, date: v})} type="date" />
            <label className="flex items-center gap-2 self-end pb-2">
              <input type="checkbox" checked={editing.published} onChange={e => setEditing({...editing, published: e.target.checked})} className="w-4 h-4" />
              <span className="text-sm font-medium">Published</span>
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={() => save(editing)} className="px-4 py-2 bg-[#D4AF37] text-[#003366] rounded-lg font-medium">Save</button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {news.map(item => (
          <div key={item.id} className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm border">
            <div className="flex items-center gap-4">
              {item.image_url && <img src={item.image_url} alt="" className="w-20 h-14 object-cover rounded" />}
              <div>
                <h4 className="font-medium text-gray-900">{item.title_de}</h4>
                <p className="text-sm text-gray-500">{item.date} • {item.category_de}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!item.published && <span className="px-2 py-1 text-xs bg-gray-200 rounded">Draft</span>}
              <button onClick={() => setEditing(item)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit size={18} /></button>
              <button onClick={() => deleteNews(item.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const JobsSection = ({ auth }) => {
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadJobs = useCallback(() => {
    fetch(`${API_URL}/api/cms/jobs`).then(r => r.json()).then(setJobs);
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const save = async (job) => {
    const method = jobs.find(j => j.id === job.id) ? 'PUT' : 'POST';
    const url = method === 'PUT' ? `${API_URL}/api/cms/jobs/${job.id}` : `${API_URL}/api/cms/jobs`;
    await fetch(url, { method, headers: { 'Authorization': auth, 'Content-Type': 'application/json' }, body: JSON.stringify(job) });
    setEditing(null);
    loadJobs();
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    await fetch(`${API_URL}/api/cms/jobs/${id}`, { method: 'DELETE', headers: { 'Authorization': auth } });
    loadJobs();
  };

  const newJob = () => setEditing({
    id: crypto.randomUUID(),
    title_de: '', title_en: '',
    type_de: '', type_en: '',
    location: 'Berlin',
    description_de: '', description_en: '',
    requirements_de: [''], requirements_en: [''],
    published: true
  });

  const updateRequirement = (lang, index, value) => {
    const key = `requirements_${lang}`;
    const newReqs = [...(editing[key] || [])];
    newReqs[index] = value;
    setEditing({...editing, [key]: newReqs});
  };

  const addRequirement = (lang) => {
    const key = `requirements_${lang}`;
    setEditing({...editing, [key]: [...(editing[key] || []), '']});
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Jobs / Karriere</h2>
        <button onClick={newJob} className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded-lg font-medium">
          <Plus size={18} />Add Job
        </button>
      </div>

      {editing && (
        <div className="p-6 bg-white rounded-xl shadow-lg border-2 border-[#D4AF37] space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <InputField label="Title (DE)" value={editing.title_de} onChange={v => setEditing({...editing, title_de: v})} />
            <InputField label="Title (EN)" value={editing.title_en} onChange={v => setEditing({...editing, title_en: v})} />
            <InputField label="Type (DE)" value={editing.type_de} onChange={v => setEditing({...editing, type_de: v})} placeholder="z.B. Vollzeit / Teilzeit" />
            <InputField label="Type (EN)" value={editing.type_en} onChange={v => setEditing({...editing, type_en: v})} placeholder="e.g. Full-time / Part-time" />
            <InputField label="Location" value={editing.location} onChange={v => setEditing({...editing, location: v})} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <InputField label="Description (DE)" value={editing.description_de} onChange={v => setEditing({...editing, description_de: v})} type="textarea" />
            <InputField label="Description (EN)" value={editing.description_en} onChange={v => setEditing({...editing, description_en: v})} type="textarea" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Requirements (DE)</span>
                <button onClick={() => addRequirement('de')} className="text-sm text-[#D4AF37]">+ Add</button>
              </div>
              {(editing.requirements_de || []).map((r, i) => (
                <input key={`req-de-${i}`} value={r} onChange={e => updateRequirement('de', i, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder={`Requirement ${i + 1}`} />
              ))}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Requirements (EN)</span>
                <button onClick={() => addRequirement('en')} className="text-sm text-[#D4AF37]">+ Add</button>
              </div>
              {(editing.requirements_en || []).map((r, i) => (
                <input key={`req-en-${i}`} value={r} onChange={e => updateRequirement('en', i, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder={`Requirement ${i + 1}`} />
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={editing.published} onChange={e => setEditing({...editing, published: e.target.checked})} className="w-4 h-4" />
            <span className="text-sm font-medium">Published</span>
          </label>
          <div className="flex gap-2">
            <button onClick={() => save(editing)} className="px-4 py-2 bg-[#D4AF37] text-[#003366] rounded-lg font-medium">Save</button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {jobs.map(job => (
          <div key={job.id} className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm border">
            <div>
              <h4 className="font-medium text-gray-900">{job.title_de}</h4>
              <p className="text-sm text-gray-500">{job.type_de} • {job.location}</p>
            </div>
            <div className="flex items-center gap-2">
              {!job.published && <span className="px-2 py-1 text-xs bg-gray-200 rounded">Draft</span>}
              <button onClick={() => setEditing(job)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit size={18} /></button>
              <button onClick={() => deleteJob(job.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ContactInfoSection = ({ auth }) => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/cms/contact-info`).then(r => r.json()).then(setData);
  }, []);

  const save = async () => {
    setSaving(true);
    await fetch(`${API_URL}/api/cms/contact-info`, {
      method: 'PUT',
      headers: { 'Authorization': auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    setSaving(false);
    alert('Saved!');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Contact Info</h2>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#003366] rounded-lg font-medium">
          <Save size={18} />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
      
      <div className="p-6 bg-white rounded-xl shadow-sm border space-y-4">
        <InputField label="Address" value={data.address} onChange={v => setData({...data, address: v})} />
        <InputField label="Phone" value={data.phone} onChange={v => setData({...data, phone: v})} />
        <InputField label="Email" value={data.email} onChange={v => setData({...data, email: v})} />
        <div className="grid md:grid-cols-2 gap-4">
          <InputField label="Hours (DE)" value={data.hours_de} onChange={v => setData({...data, hours_de: v})} />
          <InputField label="Hours (EN)" value={data.hours_en} onChange={v => setData({...data, hours_en: v})} />
        </div>
      </div>
    </div>
  );
};

const SettingsSection = ({ auth }) => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/cms/settings`).then(r => r.json()).then(setData);
  }, []);

  const save = async () => {
    setSaving(true);
    await fetch(`${API_URL}/api/cms/settings`, {
      method: 'PUT',
      headers: { 'Authorization': auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    setSaving(false);
    alert('Saved!');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Company Settings</h2>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#003366] rounded-lg font-medium">
          <Save size={18} />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
      
      <div className="p-6 bg-white rounded-xl shadow-sm border space-y-4">
        <InputField label="Company Name" value={data.name} onChange={v => setData({...data, name: v})} />
        <ImageUpload label="Logo" value={data.logo_url} onChange={v => setData({...data, logo_url: v})} auth={auth} />
        <div className="grid md:grid-cols-2 gap-4">
          <InputField label="Tagline (DE)" value={data.tagline_de} onChange={v => setData({...data, tagline_de: v})} />
          <InputField label="Tagline (EN)" value={data.tagline_en} onChange={v => setData({...data, tagline_en: v})} />
        </div>
        <h3 className="font-semibold text-gray-800 pt-4">Social Media</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <InputField label="LinkedIn URL" value={data.linkedin_url} onChange={v => setData({...data, linkedin_url: v})} />
          <InputField label="Instagram URL" value={data.instagram_url} onChange={v => setData({...data, instagram_url: v})} />
          <InputField label="Facebook URL" value={data.facebook_url} onChange={v => setData({...data, facebook_url: v})} />
        </div>
      </div>
    </div>
  );
};

const MediaSection = ({ auth }) => {
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);

  const handleUpload = async (e) => {
    const fileList = Array.from(e.target.files);
    setUploading(true);
    
    for (const file of fileList) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/cms/upload`, {
          method: 'POST',
          headers: { 'Authorization': auth },
          body: formData
        });
        const data = await res.json();
        if (data.url) {
          setFiles(prev => [...prev, { name: file.name, url: `${API_URL}${data.url}`, type: file.type }]);
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Upload failed:', err);
        }
      }
    }
    setUploading(false);
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    alert('URL copied to clipboard!');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Media Library</h2>
        <label className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded-lg font-medium cursor-pointer hover:bg-[#002244]">
          <Upload size={18} />{uploading ? 'Uploading...' : 'Upload Files'}
          <input type="file" accept="image/*,video/*" multiple onChange={handleUpload} className="hidden" />
        </label>
      </div>

      <div className="p-6 bg-white rounded-xl shadow-sm border">
        <p className="text-gray-600 mb-4">Upload images and videos here. Click on a file to copy its URL.</p>
        
        {files.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {files.map((file) => (
              <div key={file.url} className="relative group cursor-pointer" onClick={() => copyUrl(file.url)}>
                {file.type.startsWith('video') ? (
                  <video src={file.url} className="w-full h-32 object-cover rounded-lg" />
                ) : (
                  <img src={file.url} alt={file.name} className="w-full h-32 object-cover rounded-lg" />
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm">Click to copy URL</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">{file.name}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-400 py-8">No files uploaded yet in this session.</p>
        )}
      </div>
    </div>
  );
};

// Login Component
const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Authorization': getAuthHeader(username, password) }
      });

      if (res.ok) {
        onLogin(username, password);
      } else {
        setError('Invalid credentials');
      }
    } catch (err) {
      setError('Connection error');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#003366] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Settings className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">CMS Login</h1>
            <p className="text-gray-500">Content Management System</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#D4AF37] text-[#003366] font-semibold rounded-lg hover:bg-[#c4a030] transition-colors"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Main CMS Page
const CMSPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [auth, setAuth] = useState('');
  const [activeSection, setActiveSection] = useState('hero');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogin = (username, password) => {
    setAuth(getAuthHeader(username, password));
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    setAuth('');
    setIsLoggedIn(false);
  };

  if (!isLoggedIn) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'hero': return <HeroSection auth={auth} />;
      case 'services': return <ServicesSection auth={auth} />;
      case 'about': return <AboutSection auth={auth} />;
      case 'partners': return <PartnersSection auth={auth} />;
      case 'news': return <NewsSection auth={auth} />;
      case 'jobs': return <JobsSection auth={auth} />;
      case 'contact-info': return <ContactInfoSection auth={auth} />;
      case 'settings': return <SettingsSection auth={auth} />;
      case 'media': return <MediaSection auth={auth} />;
      default: return <HeroSection auth={auth} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9] flex">
      <Sidebar 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />
      
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white border-b px-4 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
              <Menu size={24} />
            </button>
            <h2 className="text-lg font-semibold text-gray-800">Content Management</h2>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
              <Eye size={18} />
              <span className="hidden sm:inline">View Site</span>
            </a>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg">
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {renderSection()}
        </main>
      </div>
    </div>
  );
};

export default CMSPage;
